import { ConnectedAccount } from './types';

interface ConnectedAccountCardProps {
  account: ConnectedAccount;
  isEditing: boolean;
  isDispatching: boolean;
  dispatchSuccess: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDispatchWorker: (id: string) => void;
}

const statusConfig = {
  active: {
    label: 'Activa',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  },
  syncing: {
    label: 'Sincronizando',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  error: {
    label: 'Error',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  },
};

export default function ConnectedAccountCard({ account, isEditing, isDispatching, dispatchSuccess, onEdit, onDelete, onDispatchWorker }: ConnectedAccountCardProps) {
  const status = statusConfig[account.status];

  return (
    <>
      <div
        className={`bg-white dark:bg-slate-800 border rounded-lg p-4 flex flex-col items-stretch gap-4 shadow-sm hover:shadow-md transition-all ${
          isEditing
            ? 'border-primary dark:border-primary ring-1 ring-primary/30'
            : 'border-slate-200 dark:border-slate-700'
        }`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`h-12 w-12 ${account.iconBg} rounded-lg flex items-center justify-center ${account.iconColor} flex-shrink-0`}
          >
            <span className="material-symbols-outlined text-2xl">{account.icon}</span>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white">{account.acquirerName}</h4>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.className}`}
              >
                {status.label}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">RUT: {account.rut}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onDispatchWorker(account.id)}
            disabled={isDispatching}
            className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md border transition-colors flex items-center justify-center gap-1.5 ${
              dispatchSuccess
                ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                : 'text-primary hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isDispatching ? (
              <>
                <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                Sincronizando...
              </>
            ) : dispatchSuccess ? (
              <>
                <span className="material-symbols-outlined text-base">check_circle</span>
                Sincronizado
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">sync</span>
                Sincronizar
              </>
            )}
          </button>
          <button
            onClick={() => onEdit(account.id)}
            className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
              isEditing
                ? 'bg-primary text-white border-primary hover:bg-blue-600'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-600'
            }`}
          >
            {isEditing ? 'Cancelar' : 'Editar'}
          </button>
          <button
            onClick={() => onDelete(account.id)}
            className="flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md border border-transparent hover:border-red-200 dark:hover:border-red-800 transition-colors"
          >
            Eliminar
          </button>
        </div>
      </div>
      {account.status === 'active' && (
        <div className="mt-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-4 py-3 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-400">
          <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5">check_circle</span>
          <span>
            <strong>Tu cuenta está conectada.</strong> Las futuras compras aparecerán automáticamente en tu cartola.
          </span>
        </div>
      )}
      {account.status === 'syncing' && (
        <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
          <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5 animate-spin">progress_activity</span>
          <span>
            Estamos cargando el historial de tus ventas. Este proceso puede tardar unos minutos. Cuando termine, podrás ver tus cuotas en la cartola.
          </span>
        </div>
      )}
      {account.status === 'error' && (
        <div className="mt-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <span className="material-symbols-outlined text-base flex-shrink-0">error</span>
          Hubo un error al sincronizar la información con su adquirente. Por favor, verifique sus credenciales e intente nuevamente.
        </div>
      )}
    </>
  );
}
